import React, { FC, ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { css, keyframes } from 'styled-components';
import { useTooltipEllipsis } from '../hooks/useTooltipEllipsis';

export type Position =
    | 'top'
    //   | "top-start"
    //   | "top-end"
    | 'bottom'
    //   | "bottom-start"
    //   | "bottom-end"
    | 'left'
    //   | "left-start"
    //   | "left-end"
    | 'right';
//   | "right-start"
//   | "right-end"

export interface Props {
    title?: string;
    html?: string | ReactNode;
    interactive?: boolean;
    position?: Position;
    children?: React.ReactNode;
    isOverflowEnabled?: boolean;
    addContainer?: boolean;
    enterDelay?: number;
    exitDelay?: number;
    maxWidth?: number;
}

interface TooltipBoxProps extends Props {
    onTooltipEnter: () => void;
    onTooltipExit: () => void;
    onInteractiveTooltipEnter: () => void;
    onInteractiveTooltipExit: () => void;
    position?: Position;
    title?: string;
    containerRef: React.RefObject<any>;
    addContainer?: boolean;
    isClosing: boolean;
    maxWidth?: number;
}

interface PropsBox {
    $containerX: number;
    $containerY: number;
    $position?: Position;
    $isClosing: boolean;
    $maxWidth?: number;
    $interactive?: boolean;
}

interface PropsArrow {
    $containerX: number;
    $containerY: number;
    $position?: Position;
}

const fadeIn = keyframes`
  from {
    opacity: 0.1;
  }

  to {
    opacity: 1;
  }
`;

const fadeOut = keyframes`
  from {
	opacity: 1;
  }

  to {
	opacity: 0;
  }
`;
const TooltipBox = styled.div<PropsBox>`
    position: fixed;
    border-radius: 5px;
    width: max-content;
    max-width: ${(props) => props.$maxWidth || '400px'};
    word-break: break-word;
    z-index: 100;
    left: ${(props) => props.$containerX}px;
    top: ${(props) => props.$containerY}px;
    -webkit-font-smoothing: antialiased;
    opacity: 0.1;
    animation: ${fadeIn} 0.1s linear forwards;

    ${(props) =>
        props.$isClosing &&
        css`
            animation: ${fadeOut} 0.1s linear forwards;
        `}

    ${(props) => props.$position === 'right' && `padding-left: 16px;`}
	${(props) => props.$position === 'left' && `padding-right: 16px;`}
	${(props) => props.$position === 'top' && `padding-bottom: 16px;`}
	${(props) => props.$position === 'bottom' && `padding-top: 16px;`}
	${(props) => !props.$interactive && `pointer-events: none;`}
`;

const TooltipBoxContent = styled.div`
    box-shadow:
        0 4px 20px 4px rgb(0 20 60 / 10%),
        0 4px 80px -8px rgb(0 20 60 / 20%);
    background-color: #fff;
    color: ${(props) => props.theme.app.primary};
    padding: 0.25rem 0.5rem;
    text-align: center;
    border-radius: 4px;
`;

const TooltipArrow = styled.div<PropsArrow>`
    position: fixed;
    border-width: 7px;
    border-style: solid;
    left: ${(props) => props.$containerX}px;
    top: ${(props) => props.$containerY}px;
    ${(props) => !props.$position && `border-color: #fff transparent transparent transparent;`}
    ${(props) => props.$position === 'top' && `border-color: #fff transparent transparent transparent;`}
	${(props) => props.$position === 'left' && `border-color: transparent transparent transparent #fff;`}
	${(props) => props.$position === 'bottom' && `border-color: transparent transparent #fff transparent;`}
	${(props) => props.$position === 'right' && `border-color: transparent #fff transparent transparent;`} /* border-bottom: 7px solid #fff; */
`;

const StyledContent = styled.div`
    padding: 4px;
    font-size: 14px;
`;

const TooltipBoxRef: FC<TooltipBoxProps> = ({
    containerRef,
    html,
    title,
    interactive,
    position = 'top',
    onTooltipEnter,
    onTooltipExit,
    onInteractiveTooltipEnter,
    onInteractiveTooltipExit,
    isClosing,
    maxWidth
}) => {
    const delay = 200;
    const tooltipRef = useRef<HTMLDivElement>(null);
    const [tooltipX, setTooltipX] = useState(0);
    const [tooltipY, setTooltipY] = useState(0);
    const [arrowX, setArrowX] = useState(0);
    const [arrowY, setArrowY] = useState(0);
    const [actualPosition, setActualPosition] = useState<Position>(position);

    useLayoutEffect(() => {
        if (!containerRef.current || !tooltipRef.current) return;

        const margin = 16;
        const containerRect = containerRef.current.getBoundingClientRect();
        const tooltipRect = tooltipRef.current.getBoundingClientRect();
        const tw = tooltipRect.width;
        const th = tooltipRect.height;
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const cx = containerRect.left + containerRect.width / 2;
        const cy = containerRect.top + containerRect.height / 2;

        // Compute tooltip position for a given side
        const compute = (pos: Position) => {
            let x = 0;
            let y = 0;
            if (pos === 'top') {
                x = cx - tw / 2;
                y = containerRect.top - th;
            } else if (pos === 'bottom') {
                x = cx - tw / 2;
                y = containerRect.bottom;
            } else if (pos === 'left') {
                x = containerRect.left - tw;
                y = cy - th / 2;
            } else {
                x = containerRect.right;
                y = cy - th / 2;
            }
            return { x, y };
        };

        // Check if a position fits on the main axis
        const fits = (pos: Position) => {
            const { x, y } = compute(pos);
            if (pos === 'top') return y >= margin;
            if (pos === 'bottom') return y + th <= vh - margin;
            if (pos === 'left') return x >= margin;
            return x + tw <= vw - margin;
        };

        const opposite: Record<Position, Position> = { top: 'bottom', bottom: 'top', left: 'right', right: 'left' };

        // Pick position: preferred → opposite → first that fits → preferred
        let resolvedPosition = position;
        if (!fits(position)) {
            if (fits(opposite[position])) {
                resolvedPosition = opposite[position];
            } else {
                const fallback = (['top', 'bottom', 'left', 'right'] as Position[]).find(fits);
                if (fallback) resolvedPosition = fallback;
            }
        }

        let { x, y } = compute(resolvedPosition);

        // Clamp cross-axis so tooltip stays in viewport
        x = Math.max(margin, Math.min(x, vw - tw - margin));
        y = Math.max(margin, Math.min(y, vh - th - margin));

        setTooltipX(x);
        setTooltipY(y);
        setActualPosition(resolvedPosition);

        // Arrow always points at container center
        const arrowCx = containerRect.left + containerRect.width / 2 - 7;
        const arrowCy = containerRect.top + containerRect.height / 2 - 7;
        if (resolvedPosition === 'top') {
            setArrowX(arrowCx);
            setArrowY(containerRect.top - 16);
        } else if (resolvedPosition === 'bottom') {
            setArrowX(arrowCx);
            setArrowY(containerRect.bottom + 2);
        } else if (resolvedPosition === 'left') {
            setArrowX(containerRect.left - 16);
            setArrowY(arrowCy);
        } else {
            setArrowX(containerRect.right + 2);
            setArrowY(arrowCy);
        }
    }, [containerRef, containerRef.current, tooltipRef, position]);

    return (
        <TooltipBox
            ref={tooltipRef}
            $containerX={tooltipX}
            $containerY={tooltipY}
            $position={actualPosition}
            $interactive={interactive}
            onMouseEnter={() => {
                onTooltipEnter();
                interactive && setTimeout(() => onInteractiveTooltipEnter(), delay);
            }}
            onMouseLeave={() => {
                onTooltipExit();
                interactive && setTimeout(() => onInteractiveTooltipExit(), delay);
            }}
            onClick={(e) => e.stopPropagation()}
            $isClosing={isClosing}
            $maxWidth={maxWidth}
        >
            <TooltipBoxContent>
                <StyledContent>{title || html}</StyledContent>
            </TooltipBoxContent>
            <TooltipArrow $containerX={arrowX} $containerY={arrowY} $position={actualPosition} />
        </TooltipBox>
    );
};

export const ZTooltip: FC<Props> = ({
    children,
    html,
    title,
    interactive,
    position,
    isOverflowEnabled,
    addContainer,
    maxWidth,
    enterDelay,
    exitDelay,
    ...rest
}) => {
    const [show, setShow] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const hoverTooltipRef = useRef<boolean>(false);
    const hoverElementRef = useRef<boolean>(false);

    const containerRef = useRef<HTMLDivElement>(null);
    const closeTimeoutRef = useRef<number | null>(null);

    const isOverflowing = isOverflowEnabled && title ? useTooltipEllipsis(title, containerRef) : false;
    const childElement = typeof children == 'string' ? <span>{children}</span> : children;

    useEffect(() => {
        const handleScroll = () => {
            setIsClosing(false);
            setShow(false);
        };

        const handleRouteChange = () => {
            setIsClosing(false);
            setShow(false);
        };

        window.addEventListener('scroll', handleScroll);
        window.addEventListener('popstate', handleRouteChange);
        window.addEventListener('beforeunload', handleRouteChange);

        // For modern browsers that support Navigation API
        if ('navigation' in window) {
            (window as any).navigation.addEventListener('navigate', handleRouteChange);
        }

        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('popstate', handleRouteChange);
            window.removeEventListener('beforeunload', handleRouteChange);

            if ('navigation' in window) {
                (window as any).navigation.removeEventListener('navigate', handleRouteChange);
            }
        };
    }, []);

    const internalInteractive = interactive ?? false;
    const handleInteractiveEnter = () => {
        if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
        }

        setIsClosing(false);
        setShow(true);
    };

    const handleInteractiveExit = () => {
        if (hoverElementRef.current) return;

        setIsClosing(true);

        closeTimeoutRef.current = setTimeout(() => {
            setShow(false);
            setIsClosing(false);
        }, 100) as unknown as number;
    };

    const isTooltipVisible = Boolean((!isOverflowEnabled || isOverflowing) && show && (title || html));

    return (
        <>
            {isTooltipVisible &&
                createPortal(
                    <TooltipBoxRef
                        containerRef={containerRef}
                        html={html}
                        title={title}
                        interactive={internalInteractive}
                        position={position}
                        onTooltipEnter={() => {
                            if (closeTimeoutRef.current) {
                                clearTimeout(closeTimeoutRef.current);
                            }

                            if (internalInteractive) {
                                hoverTooltipRef.current = true;
                            }
                        }}
                        onTooltipExit={() => {
                            if (closeTimeoutRef.current) {
                                clearTimeout(closeTimeoutRef.current);
                            }

                            hoverTooltipRef.current = false;
                        }}
                        onInteractiveTooltipEnter={handleInteractiveEnter}
                        onInteractiveTooltipExit={handleInteractiveExit}
                        isClosing={isClosing}
                        maxWidth={maxWidth}
                    ></TooltipBoxRef>,
                    document.getElementById('tooltip-container')! || document.body
                )}

            {addContainer ? (
                <div
                    ref={containerRef}
                    onMouseEnter={() => {
                        hoverElementRef.current = true;

                        if (closeTimeoutRef.current) {
                            clearTimeout(closeTimeoutRef.current);
                        }

                        closeTimeoutRef.current = setTimeout(() => {
                            setIsClosing(false);
                            setShow(true);
                        }, 100) as unknown as number;
                    }}
                    onMouseLeave={() => {
                        hoverElementRef.current = false;

                        setTimeout(() => {
                            if (hoverTooltipRef.current) return;

                            setIsClosing(true);
                            closeTimeoutRef.current = setTimeout(() => {
                                setIsClosing(false);
                                setShow(false);
                            }, 100) as unknown as number;
                        }, 1);
                    }}
                >
                    {childElement}
                </div>
            ) : (
                React.cloneElement(childElement as React.ReactElement<any>, {
                    ...rest,
                    ref: containerRef,
                    onClick: (e: React.MouseEvent) => {
                        (childElement as React.ReactElement<any>).props?.onClick?.(e);
                    },
                    onMouseEnter: () => {
                        hoverElementRef.current = true;

                        if (closeTimeoutRef.current) {
                            clearTimeout(closeTimeoutRef.current);
                        }

                        closeTimeoutRef.current = setTimeout(() => {
                            setIsClosing(false);
                            setShow(true);
                        }, 100) as unknown as number;
                    },
                    onMouseLeave: () => {
                        hoverElementRef.current = false;

                        setTimeout(() => {
                            if (hoverTooltipRef.current) return;

                            setIsClosing(true);
                            closeTimeoutRef.current = setTimeout(() => {
                                setIsClosing(false);
                                setShow(false);
                            }, 100) as unknown as number;
                        }, 1);
                    }
                })
            )}
        </>
    );
};
